import { OnInit, Input, OnDestroy, Injectable, Inject } from '@angular/core';
import { Observable, of, Subscription, combineLatest } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { DigiAIXCoreAppState } from '../app-state';
import { ActivatedRoute } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import {
  selectTableState,
  getTableData,
  getTableTitle,
  getTableSubtitle,
  getTableActions,
  getTableRowClasses,
  getTableRowStyle,
  getTableFormId,
  getTableCreateFormId,
  getTableEditFormId
} from './table.selectors';
import {
  switchMap,
  filter,
  map,
  distinctUntilChanged,
  tap
} from 'rxjs/operators';
import {
  LoadTableData,
  ImportTableData,
  ExportTableData,
  EditRow,
  RestoreRow,
  ArchiveRow,
  DeleteRow,
  DownloadRow,
  ViewRow,
  SelectRow,
  AddRow
} from './table.actions';
import { buildCollectionPath } from '../documents/document';
import { TableState, DxTableAction } from './table-definition';
import { KeyValue } from 'ngrx-forms';
import { PREFER_WINDOW } from '../tokens';
import { OpenEditFormInWindow, OpenFormInWindow } from '../forms/forms.actions';
import {
  IWithIdentifier,
  WithTableRowMeta,
  ActionButtonDefinition,
  FunctionOrConstant,
  applyContextToFunctionOrConstant
} from '@digitaix/types';

@Injectable()
export abstract class AbstractTable<
  D,
  AppState extends DigiAIXCoreAppState,
  R extends IWithIdentifier = D & IWithIdentifier
> implements OnInit, OnDestroy {
  @Input() public collationPath$: Observable<string>;
  @Input() public tableId: string;
  @Input() public collectionPathParameters: { [placeholder: string]: string };

  @Input() public data$: Observable<D[]> | null = null;
  @Input() public data: D[] | null = null;
  @Input() public collectionPath: string | null = null;
  @Input() public title: string;

  public title$!: Observable<string>;
  public subtitle$!: Observable<string>;

  public actions$!: Observable<Array<ActionButtonDefinition<[D]>>>;

  public definition: TableState<any>;

  public formId: string;

  public formId$: Observable<string>;

  public createFormId: string;

  public createFormId$: Observable<string>;

  public editFormId: string;

  public editFormId$: Observable<string>;

  protected readonly _subscriptions = new Subscription();

  constructor(
    public readonly store$: Store<AppState>,
    public readonly route: ActivatedRoute,
    public readonly logger: NGXLogger,
    @Inject(PREFER_WINDOW) public preferWindow: boolean
  ) {}

  public deleteRow(row: R): void {
    this.store$.dispatch(
      new DeleteRow<R>(this.tableId, row, this.collectionPath)
    );
  }

  public archiveRow(row: R): void {
    this.store$.dispatch(
      new ArchiveRow(this.tableId, row, this.collectionPath)
    );
  }

  public restoreRow(row: R): void {
    this.store$.dispatch(
      new RestoreRow(this.tableId, row, this.collectionPath)
    );
  }

  public editRow(row: R): void {
    if (this.preferWindow && row.id) {
      this.store$.dispatch(
        new OpenEditFormInWindow(
          this.editFormId,
          row.id,
          'AbstractTable::editRow'
        )
      );
    } else {
      this.store$.dispatch(new EditRow(this.tableId, row, this.collectionPath));
    }
  }

  public exportTableData(): void {
    this.store$.dispatch(new ExportTableData(this.tableId));
  }

  public importTableData(): void {
    this.store$.dispatch(new ImportTableData(this.tableId));
  }

  public downloadRow(row: R) {
    this.store$.dispatch(
      new DownloadRow(this.tableId, row, this.collectionPath)
    );
  }

  public addRow() {
    if (this.preferWindow) {
      this.store$.dispatch(
        new OpenFormInWindow(this.createFormId, 'AbstractTable::addRow')
      );
    } else {
      this.store$.dispatch(new AddRow(this.tableId, this.collectionPath));
    }
  }

  public viewRow(row: R) {
    this.store$.dispatch(new ViewRow(this.tableId, row, this.collectionPath));
  }

  public selectRow(row: R) {
    this.store$.dispatch(new SelectRow(this.tableId, row, this.collectionPath));
  }

  public ngOnInit(): void {
    this.logger.debug('[AbstractTable::ngOnInit]', {
      tableId: this.tableId,
      collectionPathParameters: this.collectionPathParameters,
      data: this.data,
      collectionPath: this.collectionPath
    });
    if (!this.tableId) {
      if (this.route.snapshot.data.tableId) {
        this.tableId = this.route.snapshot.data.tableId;
      }
    }
    if (this.route.snapshot.data.collectionPathParameters) {
      this.collectionPathParameters = this.route.snapshot.data.collectionPathParameters;
    }
    if (!this.tableId) {
      throw new Error('TABLE_ID_TOKEN is not set');
    }
    if (!this.collationPath$) {
      this.collationPath$ = this.store$.pipe(
        select(selectTableState(this.tableId)),
        switchMap(tableState => {
          if (tableState.getCollectionPath) {
            return tableState.getCollectionPath(this.route);
          } else {
            return of(tableState.collectionPath || null);
          }
        }),
        filter(collectionPath => !!collectionPath)
      );
    }
    this.actions$ = this.store$.select(getTableActions(this.tableId));
    this._subscriptions.add(
      this.collationPath$.subscribe(
        collectionPath => (this.collectionPath = collectionPath)
      )
    );
    this._subscriptions.add(
      this.store$
        .select(selectTableState(this.tableId))
        .pipe(filter(Boolean))
        .subscribe(definition => (this.definition = definition))
    );
    if (this.title) {
      this.title$ = of(this.title);
    } else {
      this.title$ = this.store$.select(getTableTitle(this.tableId));
    }
    this.subtitle$ = this.store$.select(getTableSubtitle(this.tableId));
    if (this.data !== null) {
      this.data$ = of(this.data);
    }

    this.formId$ = this.store$.select(getTableFormId(this.tableId));

    this._subscriptions.add(
      this.formId$.subscribe(formId => (this.formId = formId))
    );

    const applyRowClasses = (
      rowClasses: FunctionOrConstant<
        string | string[] | KeyValue,
        [D, any]
      > | null
    ) => {
      return (data: D[]): Array<WithTableRowMeta<D>> => {
        if (rowClasses) {
          return data.map(row => {
            const __classes = applyContextToFunctionOrConstant(
              rowClasses,
              row,
              this.definition
            );
            return { ...row, __classes };
          });
        }
        return data;
      };
    };

    const applyRowStyles = (
      rowStyles: FunctionOrConstant<KeyValue, [D, any]> | null
    ) => {
      return (data: D[]): Array<WithTableRowMeta<D>> => {
        if (rowStyles) {
          return data.map(row => {
            const __styles = applyContextToFunctionOrConstant(
              rowStyles,
              row,
              this.definition
            );
            return { ...row, __styles };
          });
        }
        return data;
      };
    };

    if (!this.data$) {
      this._subscriptions.add(
        this.collationPath$
          .pipe(
            map(
              collectionPath => new LoadTableData(this.tableId, collectionPath)
            )
          )
          .subscribe(this.store$)
      );

      this.data$ = combineLatest(
        this.collationPath$.pipe(distinctUntilChanged()),
        this.store$.pipe(
          select(selectTableState(this.tableId)),
          filter(tableState => !!tableState),
          distinctUntilChanged((a, b) => a.getTableData === b.getTableData)
        )
      ).pipe(
        filter(
          ([collectionPath, tableState]) =>
            !!collectionPath || tableState.getTableData !== getTableData
        ),
        distinctUntilChanged(
          ([aCollectionPath, aTableState], [bCollectionPath, bTableState]) =>
            aCollectionPath === bCollectionPath &&
            aTableState.getTableData === bTableState.getTableData
        ),
        switchMap(([collectionPath, tableState]) =>
          this.store$.pipe(
            select(
              tableState.getTableData(
                this.tableId,
                buildCollectionPath(
                  collectionPath,
                  this.collectionPathParameters
                )
              )
            )
          )
        ),
        tap(documents =>
          this.logger.debug(
            '[ExpandableTableComponent::ngOnInit] load data',
            this.collectionPath,
            documents.length
          )
        )
      );
    }

    this.data$ = combineLatest(
      this.data$,
      this.store$.select(getTableRowClasses(this.tableId)),
      this.store$.select(getTableRowStyle(this.tableId))
    ).pipe(
      map(([data, rowClasses, rowStyles]) =>
        applyRowClasses(rowClasses)(applyRowStyles(rowStyles)(data))
      )
    );

    this.createFormId$ = this.store$.select(getTableCreateFormId(this.tableId));

    this._subscriptions.add(
      this.createFormId$.subscribe(cfi => (this.createFormId = cfi))
    );

    this.editFormId$ = this.store$.select(getTableEditFormId(this.tableId));

    this._subscriptions.add(
      this.editFormId$.subscribe(cfi => (this.editFormId = cfi))
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
